'use client';

import { use, useMemo } from 'react';
import { useHits } from 'react-instantsearch';
import compact from 'lodash/compact';
import keyBy from 'lodash/keyBy';
import { BuyerListingFavorite } from '@/legacy/favorites/FavoritesViewModel';
import { ListingHit } from '@/types/ListingHit';
import { WishlistsWithMetadata } from '../../wishlist/types';
import { ListingCardHits } from '../ListingCardHits';
import { ListingCardsSkeleton } from '../ListingCardsSkeleton';
import { FlatBuyerPreferences, ListingHomePageRecommendation } from '../types';
import { AddToWishlistBanner } from './AddToWishlistBanner';
import { AddToWishlistCard } from './AddToWishlistCard';

type Props = {
  categoriesPromise: Promise<{ [key: string]: string[] }>;
  favoritesPromise: Promise<BuyerListingFavorite[]>;
  listingHomePageRecommendations: ListingHomePageRecommendation[];
  preferencesPromise: Promise<FlatBuyerPreferences>;
  wishlistsPromise: Promise<WishlistsWithMetadata>;
  isStockXSectionEnabled: boolean;
};

export const Content = ({
  categoriesPromise,
  favoritesPromise,
  listingHomePageRecommendations,
  preferencesPromise,
  wishlistsPromise,
  isStockXSectionEnabled,
}: Props) => {
  const favorites = use(favoritesPromise);
  const { items } = useHits<ListingHit>();

  // Use useMemo to prevent unnecessary recalculations on re-renders
  // Algolia returns the most recent hits first, so we need to sort them
  // based on the listingHomePageRecommendations order.
  const sortedItems = useMemo(() => {
    // Create a map
    const itemsMap = keyBy(items, (item) => +item.objectID);
    return compact(
      listingHomePageRecommendations.map((recommendation) => itemsMap[recommendation.listingId]),
    );
  }, [items, listingHomePageRecommendations]);

  // If there are recommendations but no hits, it means Algolia is still loading
  // the hits. Show a skeleton in this case.
  if (!!listingHomePageRecommendations.length && items.length === 0) {
    return <ListingCardsSkeleton hitsPerPage={8} />;
  }

  if (items.length === 0) {
    return (
      <AddToWishlistBanner
        categoriesPromise={categoriesPromise}
        preferencesPromise={preferencesPromise}
        wishlistsPromise={wishlistsPromise}
      />
    );
  }

  return (
    <>
      <ListingCardHits
        favorites={favorites}
        items={sortedItems}
        listingRecommendations={listingHomePageRecommendations}
        section="For You"
        isStockXSectionEnabled={isStockXSectionEnabled}
      />
      <AddToWishlistCard
        categoriesPromise={categoriesPromise}
        numberOfItems={items.length}
        preferencesPromise={preferencesPromise}
        wishlistsPromise={wishlistsPromise}
      />
    </>
  );
};
